/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { raiseToast } from '$components/Toast';
import { GradeType } from '$variables/types';
import { MediumUp } from '$components/Responsive';

import Button from '../Button';
import { UploadIcon } from './UploadGrades.styled';
import UploadModal from './components/UploadModal';
import mapGradesWithStudents from './utils/mapGradesWithStudents';
import parseGradesWithGradeType from './utils/parseGradesWithGradeType';
import guessColumnIndex from './utils/guessColumnIndex';

const emailHeadingMatcher = /email/i;
const gradeHeadingMatcher = /(grade|quiz)/i;

const UploadGrades = ({
  grades,
  uploadGrades,
  assignmentId,
  gradeType,
  isUploading,
  hasGradesReleased,
}) => {
  const [emailColumnIndex, setEmailColumnIndex] = useState();
  const [gradeColumnIndex, setGradeColumnIndex] = useState();
  const [file, setFile] = useState();
  const [fileError, setFileError] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(1);

  const handleCloseModal = () => {
    setModalOpen(false);
  };
  const handleOpenModal = () => {
    // ensure we start with clean state
    setStep(1);
    setFile();
    setFileError();
    setEmailColumnIndex();
    setGradeColumnIndex();
    setModalOpen(true);
  };

  const handleUploadGrades = async () => {
    const gradesWithStudentIds = mapGradesWithStudents({
      uploadedGrades: file.data,
      currentGrades: grades,
      emailColumnIndex,
      gradeColumnIndex,
    }).map(({ studentId, grade }) => ({ studentId, grade }));

    const parsedGrades = parseGradesWithGradeType({
      gradeType,
      grades: gradesWithStudentIds,
    });

    try {
      await uploadGrades({ assignmentId, grades: parsedGrades });
      handleCloseModal();
      raiseToast({ message: 'Grades uploaded!' });
    } catch (e) {
      Sentry.captureException(e);
      raiseToast({ message: 'Oops! Something went wrong.', type: 'fail' });
    }
  };

  const handleDropFile = ({ droppedFile, error }) => {
    if (droppedFile) {
      setEmailColumnIndex(
        guessColumnIndex({
          fileData: droppedFile.data,
          headingMatcher: emailHeadingMatcher,
        })
      );
      setGradeColumnIndex(
        guessColumnIndex({
          fileData: droppedFile.data,
          headingMatcher: gradeHeadingMatcher,
        })
      );
    }
    setFile(droppedFile);
    setFileError(error);
  };

  return (
    <MediumUp>
      <Button
        onClick={handleOpenModal}
        look="outlinePrimary"
        label="upload grades button"
      >
        <UploadIcon />
        Upload grades
      </Button>
      <UploadModal
        onCloseModal={handleCloseModal}
        step={step}
        file={file}
        fileError={fileError}
        onChangeStep={setStep}
        onUploadGrades={handleUploadGrades}
        isUploading={isUploading}
        open={modalOpen}
        onDropFile={handleDropFile}
        emailColumnIndex={emailColumnIndex}
        gradeColumnIndex={gradeColumnIndex}
        onSelectEmailColumn={setEmailColumnIndex}
        onSelectGradeColumn={setGradeColumnIndex}
        grades={grades}
        hasGradesReleased={hasGradesReleased}
        gradeType={gradeType}
      />
    </MediumUp>
  );
};

UploadGrades.propTypes = {
  grades: PropTypes.arrayOf(GradeType).isRequired,
  uploadGrades: PropTypes.func.isRequired,
  assignmentId: PropTypes.string.isRequired,
  gradeType: PropTypes.string.isRequired,
  isUploading: PropTypes.bool,
  hasGradesReleased: PropTypes.bool,
};

UploadGrades.defaultProps = {
  isUploading: false,
  hasGradesReleased: false,
};

export default UploadGrades;
